import type { OnInit } from '@angular/core';
import { Component, HostBinding, inject, Inject, Input } from '@angular/core';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import type { EnvConfigBrand } from '@core/config-models';
import { EnvironmentConfig } from '@core/config-models';
import { InnogyComponentRendering } from '@core/jss-models';
import { MediaObserverService } from '@innogy/shared/responsive-tools';
import { toScrollAnchorId } from '@innogy/utils-deprecated';
import type { BackgroundSettings } from '@legacy/common-ui-shared';
import {
  getBackgroundSettingsFromRendering,
  BackgroundDirective,
} from '@legacy/common-ui-shared';
import { ColumnContext } from '@legacy/common-ui-shared-interfaces';
import { map } from 'rxjs/operators';
import { NgClass, AsyncPipe } from '@angular/common';
import { RichTextWrapperComponent } from '@legacy/common-ui-utility-components';
import { JssModule } from '@sitecore-jss/sitecore-jss-angular';
import {
  PartialJumbotronComponent,
  ButtonComponent,
} from '@legacy/common-ui-partials';
import {
  TrackImpressionDirective,
  TrackLinkDirective,
} from '@analytics/events';

import type { BasicContentValues } from '../basic-content/basic-content-settings.model';
import { getBasicContentValuesFromRendering } from '../basic-content/basic-content-settings.model';
import type {
  JumbotronHeight,
  JumbotronSettings,
  JumbotronVariant,
} from './jumbotron-settings.model';
import { getJumbotronSettingsFromRendering } from './jumbotron-settings.model';
import { BasicContentVariantJumbotronComponent } from '../basic-content/variants/basic-content-variant-jumbotron/basic-content-variant-jumbotron.component';

@Component({
  selector: 'wl-jumbotron',
  templateUrl: './jumbotron-container.component.html',
  styleUrls: ['./jumbotron-container.component.scss'],
  standalone: true,
  imports: [
    PartialJumbotronComponent,
    ButtonComponent,
    TrackImpressionDirective,
    NgClass,
    TrackLinkDirective,
    RichTextWrapperComponent,
    JssModule,
    BackgroundDirective,
    BasicContentVariantJumbotronComponent,
    AsyncPipe,
  ],
})
export class JumbotronContainerComponent implements OnInit {
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input({ required: true }) rendering!: InnogyComponentRendering;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() context?: ColumnContext;

  @HostBinding('attr.id') get id() {
    return this.anchorPoint;
  }

  content!: BasicContentValues;
  backgroundSettings?: BackgroundSettings;
  jumbotronSettings!: JumbotronSettings;
  brand: EnvConfigBrand;

  constructor(
    @Inject(ENVIRONMENT_CONFIG) private readonly _config: EnvironmentConfig
  ) {
    this.brand = this._config.brand;
  }

  readonly #mediaObserverService = inject(MediaObserverService);
  isMobile$ = this.#mediaObserverService.deviceInfo$.pipe(
    map(({ isMobile }) => isMobile)
  );

  ngOnInit() {
    this.content = getBasicContentValuesFromRendering(this.rendering);
    this.jumbotronSettings = getJumbotronSettingsFromRendering(this.rendering);
    this.backgroundSettings = getBackgroundSettingsFromRendering(
      this.rendering
    );
  }

  get height(): JumbotronHeight | undefined {
    return this.jumbotronSettings?.height;
  }

  get variant(): JumbotronVariant {
    return this.jumbotronSettings?.variant;
  }

  get showMarketingHeader(): boolean {
    return this.jumbotronSettings?.marketingHeader === true;
  }

  get verticalPadding(): string | undefined {
    return this.jumbotronSettings?.verticalPadding;
  }

  get anchorPoint(): string {
    if (this.jumbotronSettings?.anchorPoint) {
      return this.jumbotronSettings?.anchorPoint;
    }
    if (this.rendering?.uid) {
      return toScrollAnchorId(this.rendering.uid);
    }
    return '';
  }
}
